import { useEffect, useMemo, useState } from "react";

import {
  type MarketplaceMappingSessionsInput,
  type MarketplaceMappingSessionsQuery,
  useMarketplaceMappingSessionsQuery,
} from "@/graphql/generated";
import {
  MAPPING_REVIEW_PAGE_SIZE,
  MAPPING_REVIEW_STATUS_OPTIONS,
  type MappingReviewStatus,
} from "@/lib/mappingReview/constants";
import { toMappingReviewMapping } from "@/lib/mappingReview/toMappingReviewMapping";

type MappingSessionRow =
  MarketplaceMappingSessionsQuery["marketplaceMappingSessions"]["items"][number];

type StatusSelectValue = MappingReviewStatus | "all";

function toMappingReviewStatus(status: string): MappingReviewStatus {
  return MAPPING_REVIEW_STATUS_OPTIONS.some((option) => option.value === status)
    ? (status as MappingReviewStatus)
    : "pending";
}

function toStatusSelectValue(status: string): StatusSelectValue {
  if (status === "all") return "all";
  return toMappingReviewStatus(status);
}

export function useMappingReview() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMarketplace, setSelectedMarketplace] = useState("all");
  const [selectedStatus, setSelectedStatus] =
    useState<StatusSelectValue>("all");
  const [statusFilter, setStatusFilter] = useState<MappingReviewStatus | null>(
    null,
  );
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, selectedMarketplace, selectedStatus, statusFilter]);

  const activeStatus =
    statusFilter ?? (selectedStatus === "all" ? null : selectedStatus);

  const queryInput = useMemo<MarketplaceMappingSessionsInput>(
    () => ({
      search: searchQuery.trim().length > 0 ? searchQuery.trim() : null,
      marketplace: selectedMarketplace === "all" ? null : selectedMarketplace,
      status: activeStatus,
      page,
      pageSize: MAPPING_REVIEW_PAGE_SIZE,
    }),
    [activeStatus, page, searchQuery, selectedMarketplace],
  );

  const { data, isFetching, isLoading } = useMarketplaceMappingSessionsQuery({
    input: queryInput,
  });

  const payload = data?.marketplaceMappingSessions;

  const allMappings = useMemo(
    () =>
      (payload?.items ?? []).map((row: MappingSessionRow) =>
        toMappingReviewMapping(
          row as unknown as Record<string, unknown>,
          row.id,
        ),
      ),
    [payload?.items],
  );

  const pageInfo = payload?.pageInfo ?? {
    page,
    pageSize: MAPPING_REVIEW_PAGE_SIZE,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false,
  };

  const kpiCounts = payload?.counts ?? {
    total: 0,
    mapped: 0,
    pending: 0,
    needsReview: 0,
    failed: 0,
  };

  const marketplaces = useMemo(
    () => ["all", ...(payload?.marketplaces ?? [])],
    [payload?.marketplaces],
  );

  const handleKPIClick = (status: MappingReviewStatus | null) => {
    setStatusFilter(statusFilter === status ? null : status);
    setSelectedStatus("all");
  };

  const handleSelectedStatusChange = (status: string) => {
    setSelectedStatus(toStatusSelectValue(status));
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    setSelectedMarketplace("all");
    setSelectedStatus("all");
    setStatusFilter(null);
    setPage(1);
  };

  const hasActiveFilters =
    !!searchQuery ||
    selectedMarketplace !== "all" ||
    selectedStatus !== "all" ||
    statusFilter != null;

  const recentMappings = useMemo(
    () =>
      [...allMappings]
        .filter((mapping) => mapping.status === "mapped" && mapping.mappedAt)
        .sort(
          (a, b) =>
            new Date(b.mappedAt ?? "").getTime() -
            new Date(a.mappedAt ?? "").getTime(),
        )
        .slice(0, 5),
    [allMappings],
  );

  const pendingMappings = useMemo(
    () =>
      allMappings.filter((mapping) => mapping.status !== "mapped").slice(0, 5),
    [allMappings],
  );

  const hasPending = allMappings.some((mapping) => mapping.status !== "mapped");

  return {
    allMappings,
    filteredMappings: allMappings,
    recentMappings,
    pendingMappings,
    hasPending,
    kpiCounts,
    marketplaces,
    isLoading: isLoading || isFetching,
    searchQuery,
    setSearchQuery,
    selectedMarketplace,
    setSelectedMarketplace,
    selectedStatus,
    setSelectedStatus: handleSelectedStatusChange,
    setStatusFilter,
    statusFilter,
    handleKPIClick,
    handleResetFilters,
    hasActiveFilters,
    pageInfo,
    page,
    setPage,
    totalCount: pageInfo.total,
  };
}
